<script setup lang="ts">
import { ref, onBeforeMount } from 'vue'
import { useFetch } from '@vueuse/core'
import BaseTypeAhead from './BaseTypeAhead.vue'
import { useI18n } from 'vue-i18n'
import { useAuthenticateSAML } from '../store/useAuthenticateSAML'
import { Notify } from 'quasar'

//translations
const { t } = useI18n({ useScope: 'global' })
const errorMessage = (key: string) => t(`form.messages.${key}`)

//component setup
const props = withDefaults(
  defineProps<{
    rules?: any[]
    modelValue: any
    label: string
    username?: { name: string; username: string }
    /**Api route to grab users from */
    route?: string
  }>(),
  {
    route: '/api/users',
    username: undefined,
    rules: undefined,
  }
)
const emit = defineEmits<{ (e: 'update:modelValue', data: any): void }>()
const currentValue = ref()
onBeforeMount(() => {
  if (props.username) {
    currentValue.value = props.username
    emit('update:modelValue', currentValue.value)
  }
})

const update = (e: any) => {
  currentValue.value = e
  emit('update:modelValue', e)
}

const { data } = props.username
  ? { data: [] }
  : useFetch(props.route, {
      async beforeFetch({ options, cancel }) {
        const { getJWT } = useAuthenticateSAML()
        if (!getJWT || !getJWT.token) cancel() //TODO: notify

        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${getJWT.token}`,
        }

        return {
          options,
        }
      },
      async onFetchError(ctx) {
        Notify.create({
          type: 'negative',
          timeout: 5000,
          position: 'bottom-left',
          message: 'An error ocurred grabbing users.',
        })
        return ctx
      },
    })
      .post({})
      .json()

const BaseTypeAheadRef = ref()
defineExpose({
  validate: () =>
    BaseTypeAheadRef.value ? BaseTypeAheadRef.value.validate() : console.log('no ref exists yet'),
})
</script>
<template>
  <BaseTypeAhead
    v-if="data"
    ref="BaseTypeAheadRef"
    :label="label"
    v-model:selected="currentValue"
    @update:selected="update"
    :dropdown-options="data"
    :search-keys="['username', 'first_name', 'last_name']"
    :rules="rules"
  >
    <template #selected-item="{ opt }">
      {{ `${opt.first_name} ${opt.last_name}` }}
    </template>
    <template #option="{ opt }">
      <q-item clickable v-close-popup @click="currentValue = opt">
        <q-item-section>
          <q-item-label>{{ `${opt.first_name} ${opt.last_name}` }} </q-item-label>
          <q-item-label caption>{{ opt.username }} </q-item-label>
        </q-item-section>
      </q-item>
    </template>
    <template #no-option>
      <q-item>
        <q-item-section class="text-grey"> {{ errorMessage('noResults') }} </q-item-section>
      </q-item>
    </template>
  </BaseTypeAhead>
</template>
